import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import Footer from '../components/Footer';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/gt687tca.png"
                alt="Forsyt"
                className="h-8 w-auto"
              />
            </div>
            <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="refund-headline">Refund Policy</h1>
          <p className="text-gray-500 mb-2">Last updated: February 2026 &middot; Version 2.0</p>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 mb-8" data-testid="refund-iso-chip">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest">ISO 27001 Certified &middot; GDPR Compliant</span>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              This Refund Policy explains when customers of <strong>Credos Forsyt Private Limited</strong>
              (operating under the brand &ldquo;Forsyt&rdquo;) may be eligible for refunds or credits. It should
              be read together with our Terms of Service, the Data Processing Agreement, and any signed order
              form. In the event of conflict between this policy and a signed order form, the order form
              prevails.
            </p>
            <p className="text-gray-700 leading-relaxed mb-8 text-sm bg-gray-50 border border-gray-200 rounded-lg p-4">
              <strong>Legal entity:</strong> Credos Forsyt Private Limited &middot;{' '}
              <strong>Registered office:</strong> New Delhi, India &middot;{' '}
              <strong>Billing contact:</strong> legal@forsyt.com
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. General Policy</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Forsyt is a subscription SaaS product for business customers.</li>
                <li>Except as described below or in a signed agreement, all fees are non‑refundable once charged, including for partially used billing periods.</li>
                <li>Some customers may be on a &ldquo;No ROI, No Payment&rdquo; or similar commercial model, which is governed by separate written terms.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Free Trials and Proof‑of‑Concepts</h2>
              <p className="text-gray-700 leading-relaxed">
                Where offered, free trials and proof‑of‑concept deployments allow you to evaluate the Service before entering a paid subscription. Fees are not charged during the defined free trial period. Once you convert to a paid plan, our standard billing and refund rules apply.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. &ldquo;No ROI, No Payment&rdquo; Programs</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>For eligible customers, Forsyt may offer a result‑linked commercial construct (for example, &ldquo;No ROI, No Payment&rdquo; or performance‑based pilots).</li>
                <li>The specific metrics, measurement windows, and data requirements will be documented in an order form or separate agreement.</li>
                <li>Where the agreed metrics are not achieved and you have met your obligations (such as integrating required systems and providing access to data), you may be entitled to fee waivers or credits instead of standard refunds.</li>
                <li>If metrics are achieved, standard subscription billing applies.</li>
                <li>If your contract includes such a program, that contract overrides this general Refund Policy to the extent of any conflict.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Auto‑Renewal &amp; Cancellation</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Subscriptions renew automatically at the then‑current pricing for the same term length unless
                either party gives written notice of non‑renewal at least <strong>sixty (60) days</strong>
                before the end of the current term. We send a renewal reminder by email and/or in‑product at
                least sixty (60) days before each renewal date.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Cancellation takes effect at the end of the then‑current term. We do not pro‑rate fees for the
                remainder of a committed term unless required by law or expressly stated in your order form.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. When Refunds May Be Considered</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Subject to applicable law and any signed agreement, Forsyt may, at its discretion, provide a partial or full refund or account credit in the following situations:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Duplicate billing:</strong> You were inadvertently billed twice for the same subscription period.</li>
                <li><strong>Technical issues fully attributable to Forsyt:</strong> Extended, verified service outages that materially prevent you from using the core Service and cannot be resolved within a reasonable period.</li>
                <li><strong>Regulatory reasons:</strong> Where law provides you with specific refund rights that cannot be waived.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Refunds or credits, if granted, will typically be limited to fees for the affected billing period and may be prorated.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Non‑Refundable Situations</h2>
              <p className="text-gray-700 leading-relaxed mb-4">Refunds are generally not provided for:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Change of mind or a decision to stop using the Service.</li>
                <li>Failure to use the Service or realise expected outcomes where Forsyt operated as contracted but internal adoption or configuration was limited.</li>
                <li>Termination due to your breach of the Terms of Service.</li>
                <li>Fees older than the last completed billing cycle, unless required by law.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                For annual or committed‑term subscriptions, cancellation typically takes effect at the end of the commitment and does not trigger a pro‑rata refund unless expressly stated in your agreement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. How to Request a Refund or Credit</h2>
              <p className="text-gray-700 leading-relaxed mb-4">To request a refund or credit:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Email <strong>legal@forsyt.com</strong> from your registered business email.</li>
                <li>Include your company name, workspace URL, invoice number(s), and a brief explanation of the issue.</li>
                <li>We may request logs or additional documentation to validate outages or billing errors.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Forsyt will review requests in good faith and aim to respond within 10 business days. Approved
                refunds are issued to the original payment method within 14 business days of approval.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                This Refund Policy is governed by the laws of the Republic of India. Any dispute arising out
                of or in connection with this policy will be subject to the exclusive jurisdiction of the
                courts of <strong>New Delhi, India</strong>, unless otherwise required by mandatory
                applicable law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to This Refund Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Refund Policy over time. Material changes will be communicated via email,
                in‑product notice, or by updating the &ldquo;Last updated&rdquo; date at least thirty (30) days
                before they take effect. Continued use of the Service after changes take effect constitutes
                acceptance of the updated policy.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RefundPolicy;
